<template>
    <b-modal content-class="argus-glass-dialog"
        v-model="visible"
        :title="modalTitle"
        size="xl"
        hide-footer
        scrollable
        modal-class="mechanic-asset-picker-modal"
        body-class="mechanic-asset-picker-modal__body"
        @shown="onShown"
        @hidden="onHidden"
    >
        <div v-if="rowTitle" class="mechanic-asset-picker__context">
            <span class="text-muted">Позиция:</span>
            <strong>{{ rowTitle }}</strong>
        </div>

        <div v-if="mode === 'search'" class="mechanic-asset-picker__search">
            <b-input-group class="mechanic-asset-picker__search-input">
                <b-input-group-prepend>
                    <b-input-group-text>
                        <i class="fas fa-search"></i>
                    </b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                    ref="searchInput"
                    v-model="search"
                    type="search"
                    placeholder="Название, группа, инв. номер, VIN, МОЛ, объект"
                    autocomplete="off"
                    @input="scheduleSearch"
                />
                <b-input-group-append v-if="search">
                    <b-button variant="outline-secondary" @click="clearSearch">
                        <i class="fas fa-times"></i>
                    </b-button>
                </b-input-group-append>
            </b-input-group>
            <b-button
                v-if="canReceiveAsset"
                variant="outline-success"
                class="mechanic-asset-picker__receipt-button"
                @click="openReceiptForm"
            >
                <i class="fas fa-plus mr-1"></i>
                Оприходовать новое
            </b-button>
        </div>

        <mechanic-asset-receipt-form
            v-if="mode === 'receipt'"
            :request-item-id="requestItemId"
            :requested-name="rowTitle"
            :requested-quantity="requestedQuantity"
            :requested-unit="requestedUnit"
            @cancel="closeReceiptForm"
            @received="onAssetReceived"
        />

        <div v-else-if="loading && !options.length" class="mechanic-asset-picker__loading">
            <b-spinner small class="mr-2"></b-spinner>
            Загрузка каталога...
        </div>

        <div v-else-if="options.length" class="mechanic-asset-picker__list">
            <button
                v-for="option in options"
                :key="option.value"
                type="button"
                class="mechanic-asset-picker__option"
                :class="{
                    'mechanic-asset-picker__option--selected': isPicked(option),
                    'mechanic-asset-picker__option--disabled': isDisabled(option)
                }"
                :disabled="isDisabled(option)"
                @click="selectOption(option)"
            >
                <div class="mechanic-asset-picker__option-main">
                    <mechanic-asset-option-preview
                        :option="option"
                        :status-text="getStatusText(option)"
                        :status-variant="getStatusVariant(option)"
                    />
                    <span
                        v-if="isUsed(option)"
                        class="mechanic-asset-picker__used-marker"
                    >
                        <i class="fas fa-link mr-1"></i>
                        Уже используется в заявке
                    </span>
                </div>
                <span class="mechanic-asset-picker__option-action">
                    <i v-if="isPicked(option)" class="fas fa-check"></i>
                    <span v-else-if="isDisabled(option)">Уже выбрана</span>
                    <span v-else>{{ getActionText(option) }}</span>
                </span>
            </button>
        </div>

        <div v-else-if="mode === 'search'" class="mechanic-asset-picker__empty">
            <div>
                <i class="fas fa-search mr-1"></i>
                Техника не найдена
            </div>
            <b-button
                v-if="canReceiveAsset"
                size="sm"
                variant="outline-success"
                class="mt-3"
                @click="openReceiptForm"
            >
                <i class="fas fa-plus mr-1"></i>
                Оприходовать новую позицию
            </b-button>
        </div>

        <div
            v-if="mode === 'search' && hasMore"
            ref="loadMoreSentinel"
            class="mechanic-asset-picker__sentinel"
            aria-hidden="true"
        ></div>

        <div
            v-if="mode === 'search' && (hasMore || multiple || (clearable && selectedId))"
            class="mechanic-asset-picker__footer"
        >
            <b-button
                v-if="multiple"
                block
                variant="primary"
                :disabled="pickedIds.length === 0"
                @click="confirmMultipleSelection"
            >
                <i class="fas fa-check mr-1"></i>
                Добавить выбранные ({{ pickedIds.length }})
            </b-button>
            <b-button
                v-if="hasMore"
                block
                variant="outline-primary"
                :disabled="loading"
                :class="{ 'mt-2': multiple }"
                @click="loadMore"
            >
                <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                Показать ещё
            </b-button>
            <b-button
                v-if="multiple && pickedIds.length"
                block
                variant="outline-secondary"
                class="mt-2"
                @click="clearPicked"
            >
                <i class="fas fa-times mr-1"></i>
                Очистить выбор
            </b-button>
            <b-button
                v-else-if="clearable && selectedId"
                block
                variant="outline-secondary"
                class="mt-2"
                @click="$emit('clear')"
            >
                <i class="fas fa-times mr-1"></i>
                Очистить выбор
            </b-button>
        </div>
    </b-modal>
</template>

<script>
import MechanicAssetOptionPreview from './MechanicAssetOptionPreview.vue';
import MechanicAssetReceiptForm from './MechanicAssetReceiptForm.vue';

const PAGE_SIZE = 50;

export default {
  name: 'MechanicAssetPickerModal',
  components: {
    MechanicAssetOptionPreview,
    MechanicAssetReceiptForm
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Выбор техники'
    },
    rowTitle: {
      type: String,
      default: ''
    },
    requestTypeId: {
      type: [Number, String],
      default: null
    },
    requestId: {
      type: [Number, String],
      default: null
    },
    requestItemId: {
      type: [Number, String],
      default: null
    },
    requestedQuantity: {
      type: [Number, String],
      default: 1
    },
    requestedUnit: {
      type: String,
      default: 'шт'
    },
    receiptEnabled: {
      type: Boolean,
      default: false
    },
    buildingProcessId: {
      type: [Number, String],
      default: null
    },
    context: {
      type: String,
      default: 'default'
    },
    selectedId: {
      type: [Number, String],
      default: null
    },
    selectedIds: {
      type: Array,
      default: () => []
    },
    excludedIds: {
      type: Array,
      default: () => []
    },
    usedIds: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      mode: 'search',
      search: '',
      options: [],
      loading: false,
      hasMore: false,
      searchTimer: null,
      loadSeq: 0,
      pickedIds: [],
      pickedOptions: {},
      autoLoadObserver: null,
      autoLoadScrollRoot: null,
      autoLoadScrollHandler: null,
      autoLoadSetupTimer: null
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    normalizedSelectedId() {
      const id = Number(this.selectedId);
      return Number.isInteger(id) && id > 0 ? id : null;
    },
    modalTitle() {
      return this.mode === 'receipt' ? 'Оприходование в справочник механиков' : this.title;
    },
    canReceiveAsset() {
      const itemId = Number(this.requestItemId);
      return this.receiptEnabled && Number.isInteger(itemId) && itemId > 0;
    },
    pickedIdSet() {
      return new Set((this.pickedIds || [])
          .map(id => Number(id))
          .filter(id => Number.isInteger(id) && id > 0));
    },
    excludedIdSet() {
      return new Set((this.excludedIds || [])
          .map(id => Number(id))
          .filter(id => Number.isInteger(id) && id > 0));
    },
    usedIdSet() {
      return new Set((this.usedIds || [])
          .map(id => Number(id))
          .filter(id => Number.isInteger(id) && id > 0));
    }
  },
  beforeDestroy() {
    this.clearSearchTimer();
    this.destroyAutoLoadObserver();
  },
  methods: {
    onShown() {
      this.mode = 'search';
      this.search = '';
      this.options = [];
      this.hasMore = false;
      this.pickedIds = (this.selectedIds || [])
          .map(id => Number(id))
          .filter(id => Number.isInteger(id) && id > 0);
      this.pickedOptions = {};
      this.loadFirstPage();
      this.$nextTick(() => {
        if (this.$refs.searchInput && this.$refs.searchInput.focus) {
          this.$refs.searchInput.focus();
        }
      });
    },
    onHidden() {
      this.clearSearchTimer();
      this.destroyAutoLoadObserver();
      this.loadSeq += 1;
      this.loading = false;
      this.mode = 'search';
    },
    clearSearchTimer() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
    },
    scheduleSearch() {
      this.clearSearchTimer();
      this.searchTimer = setTimeout(() => this.loadFirstPage(), 250);
    },
    clearSearch() {
      this.search = '';
      this.loadFirstPage();
    },
    openReceiptForm() {
      if (!this.canReceiveAsset) {
        return;
      }
      this.clearSearchTimer();
      this.destroyAutoLoadObserver();
      this.mode = 'receipt';
    },
    closeReceiptForm() {
      this.mode = 'search';
      this.$nextTick(() => {
        this.scheduleAutoLoadObserver();
        if (this.$refs.searchInput && this.$refs.searchInput.focus) {
          this.$refs.searchInput.focus();
        }
      });
    },
    onAssetReceived(asset) {
      const option = this.normalizeOption(asset);
      if (!option.value) {
        return;
      }
      this.options = [option].concat(
          this.options.filter(row => Number(row.value) !== Number(option.value))
      );
      if (this.multiple) {
        if (!this.pickedIdSet.has(Number(option.value))) {
          this.toggleOption(option);
        }
        this.closeReceiptForm();
        return;
      }
      this.$emit('select', asset);
      this.visible = false;
    },
    loadFirstPage() {
      return this.loadOptions({append: false});
    },
    loadMore() {
      return this.loadOptions({append: true});
    },
    loadMoreIfPossible() {
      if (this.hasMore && !this.loading) {
        this.loadMore();
      }
    },
    async loadOptions({append} = {append: false}) {
      if (!this.requestTypeId || (this.loading && append)) {
        return;
      }

      if (!append) {
        this.destroyAutoLoadObserver();
      }

      const offset = append ? this.options.length : 0;
      const loadSeq = this.loadSeq + 1;
      this.loadSeq = loadSeq;
      this.loading = true;
      try {
        const materials = await this.$store.dispatch('requests_getItemCatalog', {
          requestTypeId: this.requestTypeId,
          requestId: this.requestId,
          context: this.context === 'request_card_executor' ? this.context : undefined,
          buildingProcessId: this.buildingProcessId,
          q: this.search,
          limit: PAGE_SIZE,
          offset
        });
        const rows = Array.isArray(materials) ? materials : [];
        const nextOptions = rows
            .map(material => this.normalizeOption(material))
            .filter(option => option.value && option.text);
        if (loadSeq !== this.loadSeq) {
          return;
        }
        this.options = append ? this.options.concat(nextOptions) : nextOptions;
        this.hasMore = rows.length === PAGE_SIZE;
      } catch (error) {
        if (loadSeq !== this.loadSeq) {
          return;
        }
        this.options = append ? this.options : [];
        this.hasMore = false;
        this.$bvToast && this.$bvToast.toast('Не удалось загрузить каталог техники', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        if (loadSeq === this.loadSeq) {
          this.loading = false;
          this.scheduleAutoLoadObserver();
        }
      }
    },
    getAutoLoadScrollRoot() {
      const sentinel = this.$refs.loadMoreSentinel;
      return sentinel && sentinel.closest ? sentinel.closest('.modal-body') : null;
    },
    scheduleAutoLoadObserver() {
      if (this.autoLoadSetupTimer) {
        clearTimeout(this.autoLoadSetupTimer);
        this.autoLoadSetupTimer = null;
      }
      this.$nextTick(() => {
        this.autoLoadSetupTimer = setTimeout(() => {
          this.autoLoadSetupTimer = null;
          this.setupAutoLoadObserver();
        }, 0);
      });
    },
    setupAutoLoadObserver() {
      this.destroyAutoLoadObserver();
      if (!this.visible || !this.hasMore || !this.$refs.loadMoreSentinel) {
        return;
      }

      const sentinel = this.$refs.loadMoreSentinel;
      const root = this.getAutoLoadScrollRoot();

      if (root) {
        this.autoLoadScrollRoot = root;
        this.autoLoadScrollHandler = () => {
          const distanceToBottom = root.scrollHeight - root.scrollTop - root.clientHeight;
          if (distanceToBottom <= 180) {
            this.loadMoreIfPossible();
          }
        };
        root.addEventListener('scroll', this.autoLoadScrollHandler, {passive: true});
        this.autoLoadScrollHandler();
      }

      if (typeof IntersectionObserver !== 'undefined') {
        this.autoLoadObserver = new IntersectionObserver(entries => {
          const isNearBottom = entries.some(entry => entry.isIntersecting);
          if (isNearBottom) {
            this.loadMoreIfPossible();
          }
        }, {
          root,
          rootMargin: '180px 0px',
          threshold: 0.01
        });
        this.autoLoadObserver.observe(sentinel);
      }
    },
    destroyAutoLoadObserver() {
      if (this.autoLoadSetupTimer) {
        clearTimeout(this.autoLoadSetupTimer);
        this.autoLoadSetupTimer = null;
      }
      if (this.autoLoadObserver) {
        this.autoLoadObserver.disconnect();
        this.autoLoadObserver = null;
      }
      if (this.autoLoadScrollRoot && this.autoLoadScrollHandler) {
        this.autoLoadScrollRoot.removeEventListener('scroll', this.autoLoadScrollHandler);
      }
      this.autoLoadScrollRoot = null;
      this.autoLoadScrollHandler = null;
    },
    normalizeOption(material) {
      const id = Number(material && material.id);
      return {
        raw: material,
        value: Number.isInteger(id) && id > 0 ? id : null,
        text: material && material.name ? material.name : (id ? `Техника #${id}` : ''),
        assignment_details: material && material.assignment_details ? material.assignment_details : null,
        unit_of_measurement: material && material.unit_of_measurement ? material.unit_of_measurement : 'ед.',
        status: material && material.status ? material.status : null,
        status_label: material && material.status_label ? material.status_label : null,
        active_transfer: material && material.active_transfer ? material.active_transfer : null,
        catalog_name: material && (material.catalog_name || material.catalog_group_name) ? (material.catalog_name || material.catalog_group_name) : null,
        inventory_number: material && material.inventory_number ? material.inventory_number : null,
        accounting_type: material && material.accounting_type ? material.accounting_type : 'inventory',
        available_quantity: material
            ? (material.available_quantity === undefined ? material.quantity : material.available_quantity)
            : null,
        current_meter_type: material && material.current_meter_type ? material.current_meter_type : null,
        current_meter_value: material
            ? (material.current_meter_value === undefined ? null : material.current_meter_value)
            : null
      };
    },
    isSelected(option) {
      return this.normalizedSelectedId && Number(option.value) === this.normalizedSelectedId;
    },
    isPicked(option) {
      if (this.multiple) {
        return this.pickedIdSet.has(Number(option && option.value));
      }
      return this.isSelected(option);
    },
    isUsed(option) {
      const id = Number(option && option.value);
      return Number.isInteger(id) && id > 0 && this.usedIdSet.has(id);
    },
    isDisabled(option) {
      const id = Number(option && option.value);
      return Number.isInteger(id) &&
          id > 0 &&
          id !== this.normalizedSelectedId &&
          !this.pickedIdSet.has(id) &&
          this.excludedIdSet.has(id);
    },
    getActionText(option) {
      if (this.isUsed(option)) {
        return this.multiple ? 'Добавить ещё' : 'Выбрать ещё';
      }
      return this.multiple ? 'Добавить' : 'Выбрать';
    },
    selectOption(option) {
      if (!option || this.isDisabled(option)) {
        return;
      }
      if (this.multiple) {
        this.toggleOption(option);
        return;
      }
      this.$emit('select', option.raw || {
        id: option.value,
        name: option.text
      });
      this.visible = false;
    },
    toggleOption(option) {
      const id = Number(option && option.value);
      if (!Number.isInteger(id) || id <= 0) {
        return;
      }
      if (this.pickedIdSet.has(id)) {
        this.pickedIds = this.pickedIds.filter(value => Number(value) !== id);
        const next = Object.assign({}, this.pickedOptions);
        delete next[String(id)];
        this.pickedOptions = next;
        return;
      }

      this.pickedIds = this.pickedIds.concat(id);
      this.pickedOptions = Object.assign({}, this.pickedOptions, {
        [String(id)]: option.raw || {
          id: option.value,
          name: option.text
        }
      });
    },
    clearPicked() {
      this.pickedIds = [];
      this.pickedOptions = {};
    },
    confirmMultipleSelection() {
      const items = this.pickedIds
          .map(id => this.pickedOptions[String(id)])
          .filter(Boolean);
      this.$emit('confirm', items);
      this.visible = false;
    },
    getStatusText(option) {
      return option && (option.status_label || option.status) ? (option.status_label || option.status) : '';
    },
    getStatusVariant(option) {
      const status = option && option.status ? String(option.status) : '';
      const variants = {
        stock: 'success',
        in_transfer: 'info',
        on_object: 'primary',
        repair: 'warning',
        repair_acceptance: 'warning',
        ready_after_repair: 'info',
        rented_out: 'dark',
        lost: 'danger',
        written_off: 'secondary',
        sold: 'secondary',
        archive: 'secondary'
      };
      return variants[status] || 'secondary';
    }
  }
};
</script>

<style>
.mechanic-asset-picker-modal .modal-dialog {
    max-width: min(1120px, calc(100vw - 32px));
}

.mechanic-asset-picker-modal__body {
    padding: 0;
}

.mechanic-asset-picker__context {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 12px 16px 0;
    line-height: 1.25;
}

.mechanic-asset-picker__search {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
    background: #fff;
    display: flex;
    align-items: stretch;
    gap: 8px;
}

.mechanic-asset-picker__search-input {
    min-width: 0;
}

.mechanic-asset-picker__receipt-button {
    flex: 0 0 auto;
}

.mechanic-asset-picker__list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    padding: 12px 16px;
}

.mechanic-asset-picker__option {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    width: 100%;
    min-height: 78px;
    padding: 10px 12px;
    border: 1px solid #d8e1ea;
    border-radius: 6px;
    background: #fff;
    color: #212529;
    text-align: left;
    cursor: pointer;
}

.mechanic-asset-picker__option-main {
    min-width: 0;
}

.mechanic-asset-picker__option:hover,
.mechanic-asset-picker__option:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.18rem rgba(0, 123, 255, .18);
}

.mechanic-asset-picker__option--selected {
    border-color: #28a745;
    background: #f3fbf5;
    box-shadow: inset 4px 0 0 #28a745;
}

.mechanic-asset-picker__option--disabled {
    background: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
    opacity: .72;
}

.mechanic-asset-picker__used-marker {
    display: inline-flex;
    align-items: center;
    margin-top: 7px;
    padding: 3px 7px;
    border-radius: 4px;
    background: #fff3cd;
    color: #856404;
    font-size: .76rem;
    font-weight: 700;
}

.mechanic-asset-picker__option-action {
    align-self: center;
    min-width: 76px;
    padding: 5px 8px;
    border-radius: 4px;
    background: #e9f2ff;
    color: #0056b3;
    font-size: .78rem;
    font-weight: 700;
    text-align: center;
}

.mechanic-asset-picker__option--selected .mechanic-asset-picker__option-action {
    background: #d4edda;
    color: #155724;
}

.mechanic-asset-picker__option--disabled .mechanic-asset-picker__option-action {
    background: #e9ecef;
    color: #6c757d;
}

.mechanic-asset-picker__loading,
.mechanic-asset-picker__empty {
    padding: 28px 16px;
    color: #6c757d;
    text-align: center;
}

.mechanic-asset-picker__sentinel {
    height: 1px;
}

.mechanic-asset-picker__footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    padding: 12px 16px;
    border-top: 1px solid #dee2e6;
    background: #fff;
}

@media (max-width: 767.98px) {
    .mechanic-asset-picker-modal .modal-dialog {
        max-width: none;
        width: 100%;
        height: 100%;
        margin: 0;
    }

    .mechanic-asset-picker-modal .modal-content {
        height: 100%;
        border: 0;
        border-radius: 0;
    }

    .mechanic-asset-picker-modal .modal-body {
        max-height: none !important;
        overflow-y: auto;
    }

    .mechanic-asset-picker__context {
        padding: 10px 12px 0;
    }

    .mechanic-asset-picker__search {
        padding: 10px 12px;
        flex-direction: column;
    }

    .mechanic-asset-picker__receipt-button {
        width: 100%;
    }

    .mechanic-asset-picker__list {
        grid-template-columns: minmax(0, 1fr);
        gap: 8px;
        padding: 10px 12px;
    }

    .mechanic-asset-picker__option {
        grid-template-columns: minmax(0, 1fr);
        min-height: 96px;
        padding: 12px;
    }

    .mechanic-asset-picker__option-action {
        width: 100%;
        min-width: 0;
    }

    .mechanic-asset-picker__footer {
        padding: 10px 12px max(10px, env(safe-area-inset-bottom));
    }
}
</style>
